import {match} from 'ts-pattern';

import type {CustomerInsightsSection} from '../types/CustomerInsightsViewModel';

export interface BuildSectionStateInput<T> {
  data: T | undefined;
  isEmpty: (_data: T) => boolean;
  isError: boolean;
  isLoading: boolean;
  isOmitted?: boolean;
  retry: VoidFunction;
}

const buildDataSection = <T>(input: BuildSectionStateInput<T>): CustomerInsightsSection<T> => {
  const availableData = [input.data].filter((data): data is T => data !== undefined);

  return match(availableData)
    .returnType<CustomerInsightsSection<T>>()
    .with([], () => ({status: 'empty'}))
    .otherwise(([data]) =>
      match(input.isEmpty(data))
        .returnType<CustomerInsightsSection<T>>()
        .with(true, () => ({status: 'empty'}))
        .with(false, () => ({status: 'ready', data}))
        .exhaustive()
    );
};

export function buildSectionState<T>(input: BuildSectionStateInput<T>): CustomerInsightsSection<T> {
  return match(input)
    .returnType<CustomerInsightsSection<T>>()
    .with({isOmitted: true}, () => ({status: 'omitted'}))
    .with({isLoading: true}, () => ({status: 'loading'}))
    .with({isError: true}, () => {
      const availableData = [input.data].filter((data): data is T => data !== undefined);

      return match(availableData)
        .returnType<CustomerInsightsSection<T>>()
        .with([], () => ({status: 'error', retry: input.retry}))
        .otherwise(([data]) =>
          match(input.isEmpty(data))
            .returnType<CustomerInsightsSection<T>>()
            .with(true, () => ({status: 'error', retry: input.retry}))
            .with(false, () => ({status: 'degraded', data, retry: input.retry}))
            .exhaustive()
        );
    })
    .otherwise(() => buildDataSection(input));
}
